import { CLessonInCourse } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
//@ts-ignore
import { LESSONS_PAGE_SIZE } from '@yandex-int/k-common/constants';
import produce from 'immer';
import get from 'lodash/get';

import * as userConstants from 'common.components/user/constants';
import { STATISTICS_MODAL_SEEN } from 'common.components/user/user.constants';
import { actions as coursesActions } from 'store/courses/actions';

import * as constants from './constants';
import { CourseLessonsData, CourseViewActions, CourseViewStore } from './typings';

/* Формат данных drafts/lessonsActive/completedLessons/lessonsAssigned
    [courseId]: {
        sortKey: 0,
        canLoadMoreLessons: true,
        loadingStatus: LOADING_STATUSES.UNSENT,
        lessons: [],
    }
*/

const courseLessonsInitialState: CourseLessonsData = {
    sortKey: null,
    canLoadMoreLessons: true,
    loadingStatus: LOADING_STATUSES.UNSENT,
    lessons: [],
};

const initialState: CourseViewStore = {
    statisticsModalClosed: false,
    drafts: {},
    completedLessons: {},
    activeLessons: {},
    assignedLessons: {},
    isAssignedLessonEvaluable: false,
};

const courseViewReducer = (state: CourseViewStore = initialState, action: CourseViewActions) => {
    // eslint-disable-next-line complexity
    return produce(state, (draft) => {
        switch (action.type) {
            case constants.GET_DRAFTS_REQUEST: {
                const { courseId } = action;

                if (!state.drafts[courseId]) {
                    draft.drafts[courseId] = { ...courseLessonsInitialState };
                }

                draft.drafts[courseId].loadingStatus = LOADING_STATUSES.LOADING;
                draft.drafts[courseId].canLoadMoreLessons = true;

                break;
            }

            case constants.GET_DRAFTS_SUCCESS: {
                const { courseId, lessons, oldSortKey, newSortKey } = action;
                const {
                    lessons: stateLessons = [],
                    sortKey: stateSortKey,
                    canLoadMoreLessons: stateCanLoadMoreLessons,
                } = state.drafts[courseId] || {};

                // если ответ пришел не с тем ключом сортировки или мы уже успели все догрузить
                // ничего не делаем - это мог быть повторный (лишний) запрос
                if (stateSortKey !== oldSortKey || !stateCanLoadMoreLessons) {
                    break;
                }

                draft.drafts[courseId].lessons = [...stateLessons, ...lessons];
                draft.drafts[courseId].sortKey = newSortKey;
                draft.drafts[courseId].canLoadMoreLessons = lessons.length === LESSONS_PAGE_SIZE;
                draft.drafts[courseId].loadingStatus = LOADING_STATUSES.SUCCESS;

                break;
            }

            case constants.GET_DRAFTS_ERROR: {
                const { courseId } = action;

                if (!state.drafts[courseId]) {
                    draft.drafts[courseId] = { ...courseLessonsInitialState };
                }

                draft.drafts[courseId].loadingStatus = LOADING_STATUSES.ERROR;

                break;
            }

            // попап прячется, когда выставляется хинт, но при медленном интернете это происходит долго
            // выглядит как зависший интерфейс. Поэтому при отправке хинта мы сразу прячем соответствующий попап,
            // а когда загрузка завершена - сбрасываем флаг, чтобы не мешал при повторном показе хинта
            // @ts-ignore
            case userConstants.ADD_VIEWED_HINT_REQUEST: {
                // @ts-ignore
                if (action.id === STATISTICS_MODAL_SEEN) {
                    draft.statisticsModalClosed = true;
                }
                break;
            }

            // @ts-ignore
            case userConstants.ADD_VIEWED_HINT_SUCCESS:
            // @ts-ignore
            // eslint-disable-next-line no-fallthrough
            case userConstants.ADD_VIEWED_HINT_ERROR: {
                // @ts-ignore
                if (action.id === STATISTICS_MODAL_SEEN) {
                    draft.statisticsModalClosed = false;
                }
                break;
            }

            case constants.GET_COMPLETED_LESSONS_REQUEST: {
                const { courseId } = action;

                if (!state.completedLessons[courseId]) {
                    draft.completedLessons[courseId] = { ...courseLessonsInitialState };
                }

                draft.completedLessons[courseId].loadingStatus = LOADING_STATUSES.LOADING;

                break;
            }
            case constants.GET_COMPLETED_LESSONS_SUCCESS: {
                const { courseId, lessons, oldSortKey, newSortKey } = action;
                const {
                    lessons: stateLessons = [],
                    sortKey: stateSortKey,
                    canLoadMoreLessons: stateCanLoadMoreLessons,
                } = state.completedLessons[courseId] || {};

                // если ответ пришел не с тем ключом сортировки или мы уже успели все догрузить
                // ничего не делаем - это мог быть повторный (лишний) запрос
                if (stateSortKey !== oldSortKey || !stateCanLoadMoreLessons) {
                    break;
                }

                draft.completedLessons[courseId].loadingStatus = LOADING_STATUSES.SUCCESS;
                draft.completedLessons[courseId].lessons = [...stateLessons, ...lessons];
                draft.completedLessons[courseId].canLoadMoreLessons = lessons.length === LESSONS_PAGE_SIZE;
                draft.completedLessons[courseId].sortKey = newSortKey;

                break;
            }
            case constants.GET_COMPLETED_LESSONS_ERROR: {
                const { courseId } = action;

                draft.completedLessons[courseId].loadingStatus = LOADING_STATUSES.ERROR;

                break;
            }

            case constants.GET_ACTIVE_LESSONS_REQUEST: {
                const { courseId } = action;

                if (!state.activeLessons[courseId]) {
                    draft.activeLessons[courseId] = { ...courseLessonsInitialState };
                }

                draft.activeLessons[courseId].loadingStatus = LOADING_STATUSES.LOADING;

                break;
            }
            case constants.GET_ACTIVE_LESSONS_SUCCESS: {
                const { courseId, lessons, sortKey } = action;
                let oldLessons: Array<CLessonInCourse> = [];

                if (!state.activeLessons[courseId]) {
                    draft.activeLessons[courseId] = { ...courseLessonsInitialState };
                } else {
                    oldLessons = state.activeLessons[courseId].lessons;
                }

                draft.activeLessons[courseId].loadingStatus = LOADING_STATUSES.SUCCESS;
                draft.activeLessons[courseId].lessons = [...oldLessons, ...lessons];
                draft.activeLessons[courseId].canLoadMoreLessons = lessons.length === LESSONS_PAGE_SIZE;
                draft.activeLessons[courseId].sortKey = sortKey;

                break;
            }
            case constants.GET_ACTIVE_LESSONS_ERROR: {
                const { courseId } = action;

                draft.activeLessons[courseId].loadingStatus = LOADING_STATUSES.ERROR;

                break;
            }

            case constants.GET_ASSIGNED_LESSONS_REQUEST: {
                const { courseId } = action;

                if (!state.assignedLessons[courseId]) {
                    draft.assignedLessons[courseId] = { ...courseLessonsInitialState };
                }

                draft.assignedLessons[courseId].loadingStatus = LOADING_STATUSES.LOADING;

                break;
            }
            case constants.GET_ASSIGNED_LESSONS_SUCCESS: {
                const { courseId, lessons, sortKey } = action;
                let oldLessons: Array<CLessonInCourse> = [];

                if (!state.assignedLessons[courseId]) {
                    draft.assignedLessons[courseId] = { ...courseLessonsInitialState };
                } else {
                    oldLessons = state.assignedLessons[courseId].lessons;
                }

                draft.assignedLessons[courseId].loadingStatus = LOADING_STATUSES.SUCCESS;
                draft.assignedLessons[courseId].lessons = [...oldLessons, ...lessons];
                draft.assignedLessons[courseId].canLoadMoreLessons = lessons.length === LESSONS_PAGE_SIZE;
                draft.assignedLessons[courseId].sortKey = sortKey;

                break;
            }
            case constants.GET_ASSIGNED_LESSONS_ERROR: {
                const { courseId } = action;

                draft.assignedLessons[courseId].loadingStatus = LOADING_STATUSES.ERROR;

                break;
            }

            case constants.CLEAR_LESSONS: {
                draft.completedLessons = {};
                draft.drafts = {};
                draft.activeLessons = {};
                draft.assignedLessons = {};

                break;
            }

            case constants.MARK_CLESSON_AS_READ: {
                const { courseId, clessonId } = action;
                if (courseId && clessonId) {
                    const lessons = get(draft.completedLessons[courseId], 'lessons', []);
                    const lesson = lessons.find(({ id }) => id === clessonId);

                    if (lesson) {
                        lesson.is_read = true;
                    }
                }

                break;
            }

            case coursesActions.clearCourseLessons.type: {
                //@ts-ignore
                const { courseId } = action.payload;

                draft.activeLessons[courseId] = { ...courseLessonsInitialState };
                draft.assignedLessons[courseId] = { ...courseLessonsInitialState };
                draft.completedLessons[courseId] = { ...courseLessonsInitialState };
                draft.drafts[courseId] = { ...courseLessonsInitialState };

                break;
            }

            default:
                break;
        }
    });
};

export default courseViewReducer;
